import { NodeResults, RunDto, RunResults } from "@custom-types/modelRun"
import {
  ProbabilityDensityChartOptions,
  ProbabilityDensityContainer,
} from "@features/results/Result/ChartsContainer/ProbabilityDensityContainer"
import { conditionedResultResponseToProbabilityDensityChartResults } from "@features/results/Result/ThresholdChartContainer/helpers"
import ThresholdForm, {
  ThresholdFormValues,
} from "@features/results/Result/ThresholdChartContainer/ThresholdForm"
import { CircularProgress, Grid, Typography } from "@mui/material"
import { GraphNodeData, GraphNodeEdgeData } from "@store/services/converter/types"
import { usePostConditionedResultMutation } from "@store/services/runService"
import { enqueueSnackbar } from "notistack"
import { useEffect, useState } from "react"
import { useDebounceCallback } from "usehooks-ts"

export type ThresholdChartContainerProps = {
  graphData: GraphNodeEdgeData
  nodesData: GraphNodeData[]
  nodesResults: NodeResults
  isLoading: boolean
  run: RunDto
  localStoragePrefix: string
  defaultGivenEventNodeLabel?: string
  defaultEventNodeLabel?: string
  chartOptions?: ProbabilityDensityChartOptions
  onlyChart?: boolean
  maintainAspectRatio?: boolean
  showMinMaxBoundary?: boolean
  legendPosition?: "top" | "bottom"
  showLegend?: boolean
  hiddenDatasets?: string[]
  onLegendClick?: (datasetLabel: string) => void
  lag: number
}

const ThresholdChartContainer = (props: ThresholdChartContainerProps) => {
  const [results, setResults] = useState<RunResults>()
  const [chartTitle, setChartTitle] = useState("")
  const [errorText, setErrorText] = useState("")

  const [postCondition, postConditionMutation] = usePostConditionedResultMutation()

  useEffect(() => {
    if (postConditionMutation.isError) {
      enqueueSnackbar("Failed to load condition results", { variant: "error" })
    }

    if (!postConditionMutation.data) return

    if ("error" in postConditionMutation.data) {
      setErrorText(postConditionMutation.data.error)
      setResults(undefined)
      return
    }

    const parsedResults = conditionedResultResponseToProbabilityDensityChartResults(
      postConditionMutation.data,
      postConditionMutation.originalArgs!.condition,
      props.run.key,
    )

    setErrorText("")
    setResults(parsedResults)
    setChartTitle(postConditionMutation.data.blurb)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [postConditionMutation.data, postConditionMutation.isError])

  const handleThresholdFormSubmit = useDebounceCallback((formValues: ThresholdFormValues) => {
    postCondition({ resultKey: props.run.key, condition: formValues })
  }, 500)

  if (props.isLoading) {
    return (
      <Grid container justifyContent="center">
        <Grid item>
          <CircularProgress />
        </Grid>
      </Grid>
    )
  }

  if (Object.values(props.nodesResults).length < 2) {
    return <></>
  }

  return (
    <Grid container direction="column" gap={2}>
      <Grid item sx={{ width: "100%" }}>
        <ThresholdForm
          graphData={props.graphData}
          nodesData={props.nodesData}
          nodesResults={props.nodesResults}
          run={props.run}
          onSubmit={handleThresholdFormSubmit}
          defaultGivenEventNodeLabel={props.defaultGivenEventNodeLabel}
          defaultEventNodeLabel={props.defaultEventNodeLabel}
          lag={props.lag}
          showMinMaxBoundary={props.showMinMaxBoundary}
        />
      </Grid>

      {postConditionMutation.isLoading && (
        <Grid item alignSelf="center">
          <CircularProgress />
        </Grid>
      )}

      {Object.values(results ?? {}).length > 0 && !postConditionMutation.isLoading && (
        <Grid item sx={{ width: "100%" }}>
          <ProbabilityDensityContainer
            runs={[props.run]}
            isFetchingGetRunFilterNode={false}
            rawResultDataFilterNodes={results}
            lagShift={0} // we are passing already filtered data, no need for shifting it
            selectedNode={null}
            localStoragePrefix={props.localStoragePrefix}
            showYearSlider={false}
            chartTitle={chartTitle}
            getDatasetLabel={(data) => data.node_key}
            chartOptions={props.chartOptions}
            onlyChart={props.onlyChart}
            maintainAspectRatio={props.maintainAspectRatio}
            legendPosition={props.legendPosition}
            showLegend={props.showLegend}
            hiddenDatasets={props.hiddenDatasets}
            onLegendClick={props.onLegendClick}
          />
        </Grid>
      )}

      {errorText && (
        <Grid item>
          <Typography>{errorText}</Typography>
        </Grid>
      )}
    </Grid>
  )
}

export default ThresholdChartContainer
